<template>
    <div v-if="hierarchyState.Enabled !== undefined" :class="{disabled: waitingForResponse}">
        <div class="hierarchy-group-checkbox">
            <input type="checkbox" id="hierarchy-check-box" v-model="hierarchyState.Enabled">
            <label for="hierarchy-check-box">Enable Hierarchy synchronization</label>
        </div>
        <transition name="fade">
            <div v-if="hierarchyState.Enabled" class="hierarchy-container">
                <div class="hierarchy-links">
                    <label>Jira link type to replicate Parent / Child relation from TFS:</label>
                    <div class="hierarchy-group-select">
                        <select v-model="hierarchyState.JiraLinkId" :disabled="!hierarchyState.Enabled">
                            <option v-for="linkType in availableJiraValues" :value="linkType.JiraLinkType">
                                {{linkType.title}}
                            </option>
                        </select>
                        <button @click="invertJiraLink()" class="tfs4jira-rounded-button reverse">
                            <img :src="imagePath +'reverse-icon.svg'">
                        </button>
                    </div>
                    <notification class="info" icon-name="info-circle" icon-class="info-message">
                        <p>
                            <b>Link types used for hierarchy synchronization will not be available for
                                <span v-if="wizardView">manual mapping:</span>
                                <a v-else :href="`${contextPath()}/Profiles/${profileGuid}/mappings/links`">
                                    manual mapping:
                                </a>
                            </b>
                        </p>
                       <ul>
                           <li>TFS "Parent / Child" link</li>
                           <li v-if="selectedJiraLinkType !== '' && selectedJiraLinkType !== 'None'">Jira "{{selectedJiraLinkType}}" link</li>
                       </ul>
                    </notification>
                    <div class="hierarchy-group-checkbox epic-check-box">
                        <input type="checkbox" id="epic-link-used" v-bind:disabled="!hierarchyState.Enabled" v-model="hierarchyState.UseEpicLinks">
                        <label for="epic-link-used">
                            For Jira Epics use default method ({{ isNextGen ? "Parent" : "Epic Link" }} field)
                            <span v-if="selectedJiraLinkType !== 'None' && selectedJiraLinkType !== ''"> instead of <b>{{selectedJiraLinkType}}</b></span>.
                        </label>
                    </div>
                </div>
                <div v-if="!wizardView">
                    <notification
                        v-if="hierarchyState.UseEpicLinks && !isEpicTypeMapped"
                        class="important"
                        icon-name="exclamation-circle"
                        icon-class="attention-message"
                    >
                        <p>Jira Epic issue is not mapped to any TFS work item.</p>
                        <p>
                            <a :href="`${contextPath()}/Tfs/Index/${profileGuid}?mappings=1&mappingid=${issueTypeMappingId}&subtasks=false`">
                                Map now
                            </a>
                        </p>
                    </notification>
                    <notification
                        v-if="!isNextGen && hierarchyState.UseEpicLinks && !isEpicNameMapped"
                        class="important"
                        icon-name="exclamation-circle"
                        icon-class="attention-message"
                    >
                        <p>Jira Epic Name field is not mapped to any TFS field.</p>
                        <p>
                            <a :href="`${contextPath()}/Tfs/Index/${profileGuid}?mappings=1&otherfields=1`">
                            Map now
                            </a>
                        </p>
                    </notification>
                </div>
            </div>
        </transition>
    </div>
</template>
<script>
    import { mapGetters, mapActions } from 'vuex';
    import { NAMESPACE as HIERARCHY_NAMESPACE } from '../store/modules/hierarchy.js';
    import { NAMESPACE as APP_NAMESPACE } from '../store/modules/app.js';
    import Notification from './Notification';

    export default {
        components: {
            Notification
        },
        props: [
            'profileGuid',
            'loadOnInit',
            'wizardView'
        ],
        data() {
            return {
                mountCallback: function() {},
                issueTypeMappingId: null,
                currentJiraLinkId: null
            }
        },
        computed: mapGetters({
            selectedJiraLinkType: HIERARCHY_NAMESPACE.getters.JIRA_LINK_TITLE,
            hierarchyState: HIERARCHY_NAMESPACE.getters.HIERARCHY_STATE,
            availableJiraValues: HIERARCHY_NAMESPACE.getters.AVAILABLE_JIRA_VALUES,
            isEpicNameMapped: HIERARCHY_NAMESPACE.getters.IS_EPIC_NAME_MAPPED,
            isEpicTypeMapped: HIERARCHY_NAMESPACE.getters.IS_EPIC_TYPE_MAPPED,
            isNextGen: HIERARCHY_NAMESPACE.getters.IS_NEXT_GEN,
            waitingForResponse: HIERARCHY_NAMESPACE.getters.WAITING_FOR_RESPONSE,
            imagePath: APP_NAMESPACE.getters.IMAGE_PATH
        }),
        watch: {
            hierarchyState: {
                handler(newHierarchyState, oldHierarchyState) {
                    if (oldHierarchyState.Enabled !== undefined) {
                        let jiraLinkTitleAsObject = {
                            JiraLinkTitle: findJiraLinkTitle(this.availableJiraValues, this.hierarchyState.JiraLinkId)
                        };
                        this.updateHierarchy(Object.assign({}, this.hierarchyState, jiraLinkTitleAsObject));
                    }
                },
                deep: true
            }
        },
        mounted: function() {
            this.setUpService({
                prefix: this.contextPath() + '/api/profiles/' + this.profileGuid
            });
            if (this.loadOnInit === 'true') {
                this.initComponent();
            }
            this.registerListener({
                on: 'RefreshHierarchyComponent',
                handler: function(notification) {
                    this.initComponent();
                }.bind(this)
            });
            this.registerListener({
                on: 'SetIssueTypeMappingId',
                handler: function(notification) {
                    this.issueTypeMappingId = notification.id;
                }.bind(this)
            });
            this.registerListener({
                //to test window.Spartez.Shared.sendNotification('FieldMappingChanged', {});
                on: 'FieldMappingChanged',
                handler: function(notification) {
                    this.fetchHierarchy();
                }.bind(this)
            });
        },
        methods: {
            ...mapActions({
                setUpService: HIERARCHY_NAMESPACE.actions.SET_UP_SERVICE,
                updateHierarchy: HIERARCHY_NAMESPACE.actions.UPDATE_HIERARCHY_STATE,
                fetchHierarchy: HIERARCHY_NAMESPACE.actions.FETCH_HIERARCHY_STATE,
                registerListener: APP_NAMESPACE.actions.ADD_LISTENER
            }),
            ...mapGetters({
                contextPath: APP_NAMESPACE.getters.CONTEXT_PATH
            }),
            initComponent: async function() {
                this.fetchHierarchy();
                this.mountCallback();
            },
            invertJiraLink: function() {
                this.hierarchyState.JiraLinkIsInverted = !this.hierarchyState.JiraLinkIsInverted;
            }
        }
    }

    const findJiraLinkTitle = function(jiraLinksArray, selectedJiraLinkId) {
        if (!jiraLinksArray) {
            return '';
        }
        let selectedLink = jiraLinksArray.find((link) => link.JiraLinkType === selectedJiraLinkId);
        return (selectedLink) ? selectedLink.title : '';
    };
</script>
<style scoped>
.hierarchy-container {
    width: 100%;
    margin-left: 2em;
}
.hierarchy-links{
    width: 100%;
    margin: 1em 0 1em 0;
}
.hierarchy-links select {
    width: 240px;
    border: 1px solid #ccc;
    padding: .1em;
    font-size: 1rem;
    color: #444;
}
.hierarchy-links label {
    line-height: 1.5rem;
}
.hierarchy-group-checkbox input[type="checkbox"],
.hierarchy-group-checkbox label {
    cursor: pointer;
    font-size: 16px;
}
.hierarchy-group-checkbox label b {
    font-size: .9rem;
}
.hierarchy-group-select{
    margin-top: .2em;
}
.hierarchy-group-select button {
    padding: .1em;
    width: 1.4em;
    height: 1.4em;
    margin-bottom: .4em;
}
label {
    font-size: 1em;
    font-family: sans-serif;
    color: #444;
}
button img {
    width: 1em;
    height: 1em;
}
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s ease;
    opacity: 1;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
.tfs4jira-group-checkbox {
    margin-top:.5rem;
}
#hierarchy-check-box {
    margin: .5em .5em 0 0;
}
.epic-check-box {
    margin-top: 1em;
}
.disabled {
    pointer-events: none;
    opacity: 0.4;
}
</style>



// WEBPACK FOOTER //
// src/components/Hierarchy.vue